import { forwardRef, useEffect, useState } from 'react'
import { type CustomContentProps } from 'notistack'
import * as S from './ProgressSnackbar.styles'
import SwipeableSnackbar from '../SwipeableSnackbar/SwipeableSnackbar'
import { type ProgressSnackbarExtraOptions } from '../types'

const TARGET_PROGRESS = 80 // ease to 80%, then hold
const DEFAULT_DURATION_MS = 60 * 1000
const UPDATE_INTERVAL_MS = 100

// Generic generation-time toast: a loading bar that eases toward 80% over an
// estimated time (or 60s if unknown). It's shown for the whole task and dismissed
// by the caller on completion, so the bar never needs to reach 100% itself — we
// can't track real backend progress. The caller supplies the label + testId.
const ProgressSnackbar = forwardRef<
  HTMLDivElement,
  ProgressSnackbarExtraOptions & CustomContentProps
>(({ id, label, testId, estimatedTime, name, progressVariant }, ref) => {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const totalDuration = estimatedTime
      ? estimatedTime * 1000
      : DEFAULT_DURATION_MS
    const startTime = Date.now()
    const interval = setInterval(() => {
      const next = Math.min(
        ((Date.now() - startTime) / totalDuration) * TARGET_PROGRESS,
        TARGET_PROGRESS
      )
      setProgress(next)
      if (next >= TARGET_PROGRESS) clearInterval(interval)
    }, UPDATE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [estimatedTime])

  return (
    <SwipeableSnackbar
      id={id}
      name={name}
      text={label}
      isPassThrough={progressVariant === 'fill'}
      ref={ref}
    >
      <S.Pill data-testid={testId} $variant={progressVariant}>
        <S.Label>{label}</S.Label>
        <S.Bar $variant={progressVariant}>
          <S.BarTrack $variant={progressVariant} />
          <S.BarFill
            style={{ width: `${progress}%` }}
            data-testid={`${testId}-fill`}
          />
        </S.Bar>
      </S.Pill>
    </SwipeableSnackbar>
  )
})

export default ProgressSnackbar
